import React from 'react'

import type { InlineResult, PublishResult, SelectOption } from '../../console-types'
import { cn } from '../../lib/utils'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

export const fieldsetClass =
  'rounded-lg border border-border/60 bg-card/60 p-5 shadow-sm'
export const legendClass =
  'px-1 font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground'

const detailsClass =
  'rounded-lg border border-border/60 bg-card/60 shadow-sm transition-colors open:border-primary/30 open:bg-card/80'
const detailsBodyClass = 'grid gap-4 px-5 pb-5 pt-2'
const labelClass = 'flex flex-col gap-1.5'
const labelTextClass = 'text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'

export function messageKindBadgeVariant(kind: string): React.ComponentProps<typeof Badge>['variant'] {
  switch (kind) {
    case 'PACS008':
      return 'secondary'
    case 'PACS002':
      return 'warning'
    case 'PACS004':
      return 'default'
    default:
      return 'outline'
  }
}

export function messageDirectionBadgeVariant(direction: string): React.ComponentProps<typeof Badge>['variant'] {
  if (direction.toUpperCase() === 'INBOUND') return 'success'
  if (direction.toUpperCase() === 'OUTBOUND') return 'destructive'
  return 'outline'
}

export function formatTime(value: string) {
  return new Date(value).toLocaleTimeString('pt-BR')
}

export function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <label className={labelClass}>
      <span className={labelTextClass}>{label}</span>
      {children}
    </label>
  )
}

export function DetailsSection({
  summary,
  hint,
  children,
}: {
  summary: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <details className={detailsClass}>
      <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-3.5 marker:hidden">
        <span className="font-display text-[11px] font-bold uppercase tracking-widest text-foreground/80">
          {summary}
        </span>
        {hint ? (
          <span className="text-xs font-normal normal-case tracking-normal text-muted-foreground">
            — {hint}
          </span>
        ) : null}
      </summary>
      <div className={detailsBodyClass}>{children}</div>
    </details>
  )
}

export function StatPill({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-card/60 p-4 shadow-sm">
      <div className="rounded-md bg-primary/10 p-2 text-primary">{icon}</div>
      <div className="space-y-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs leading-5 text-muted-foreground">{description}</div>
      </div>
    </div>
  )
}

export function ResultBlock({
  result,
}: {
  result: PublishResult | InlineResult | null
}) {
  if (!result) return null

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        result.variant === 'error'
          ? 'border-red-500/30 bg-red-500/8'
          : 'border-emerald-500/30 bg-emerald-500/8'
      )}
    >
      {'title' in result ? (
        <div
          className={cn(
            'mb-3 font-display text-sm font-bold',
            result.variant === 'error' ? 'text-red-400' : 'text-emerald-400'
          )}
        >
          {result.title}
        </div>
      ) : null}
      <pre className="overflow-auto rounded border border-border/50 bg-background/60 px-4 py-3 font-mono text-xs leading-6 text-foreground/90">
        {result.body}
      </pre>
    </div>
  )
}

export function FormSelectField({
  name,
  value,
  onValueChange,
  placeholder,
  options,
  submitValue,
  className,
}: {
  name: string
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  options: SelectOption[]
  submitValue?: string
  className?: string
}) {
  return (
    <>
      <input type="hidden" name={name} value={submitValue ?? value} />
      <Select value={value || undefined} onValueChange={onValueChange}>
        <SelectTrigger className={cn('bg-input', className)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  )
}

export function ControlledSelectField({
  value,
  onValueChange,
  placeholder,
  options,
  className,
}: {
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  options: SelectOption[]
  className?: string
}) {
  return (
    <Select value={value || undefined} onValueChange={onValueChange}>
      <SelectTrigger className={cn('bg-input', className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
